import { PLATFORM_LABELS } from '@shopx-distribution/shared';
import { useState } from 'react';

import {
  useConnections,
  useCreateTarget,
  useDeleteTarget,
  useRemoteTargets,
  useTargets,
} from '@/api/hooks.js';
import { PlatformIcon } from '@/components/PlatformIcon.js';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  SectionHeading,
} from '@/components/ui.js';

/**
 * Targets are what users pick in the Experience page: a connection plus the
 * account or library assets are created under.
 *
 * Because targets are per tenant, a staging tenant can point at a Google Ads
 * test account while the production tenant points at the live customer id, with
 * no branching anywhere in the code.
 */
export function TargetsPage() {
  const connections = useConnections();
  const targets = useTargets(true);
  const createTarget = useCreateTarget();
  const deleteTarget = useDeleteTarget();

  const [connectionId, setConnectionId] = useState<string>('');
  const [name, setName] = useState('');
  const [externalTargetId, setExternalTargetId] = useState('');

  const remoteTargets = useRemoteTargets(connectionId || null);

  const selectedOption = remoteTargets.data?.options.find(
    (option) => option.externalTargetId === externalTargetId,
  );

  const submit = async () => {
    await createTarget.mutateAsync({
      connectionId,
      name: name.trim(),
      externalTargetId: externalTargetId.trim(),
      // Carried over from discovery: an account found under a manager can only
      // be uploaded to with that manager as `login-customer-id`, and this is
      // the only point where which manager it was found under is still known.
      ...(selectedOption?.loginCustomerId
        ? { config: { loginCustomerId: selectedOption.loginCustomerId } }
        : {}),
    });
    setName('');
    setExternalTargetId('');
  };

  if (targets.isLoading) return <Loading label="Loading targets…" />;

  return (
    <div className="space-y-8">
      <section>
        <SectionHeading
          title="Add a target"
          description="Pick a connected system, then the account or library assets should land in."
        />
        <Card className="space-y-4 p-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Connection</span>
            <select
              value={connectionId}
              onChange={(event) => {
                setConnectionId(event.target.value);
                setExternalTargetId('');
              }}
              className="w-full rounded-brand border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select a connection…</option>
              {connections.data?.connections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.displayName} ({PLATFORM_LABELS[connection.platform]})
                </option>
              ))}
            </select>
          </label>

          {connectionId ? (
            <label className="block">
              <span className="mb-1 block text-sm font-medium">Account</span>
              {remoteTargets.isLoading ? (
                <p className="text-sm text-slate-500">Loading accessible accounts…</p>
              ) : remoteTargets.error ? (
                <ErrorNotice error={remoteTargets.error} />
              ) : (
                <select
                  value={externalTargetId}
                  onChange={(event) => {
                    setExternalTargetId(event.target.value);
                    const option = remoteTargets.data?.options.find(
                      (candidate) => candidate.externalTargetId === event.target.value,
                    );
                    if (option && !name) setName(option.name);
                  }}
                  className="w-full rounded-brand border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">Select an account…</option>
                  {remoteTargets.data?.options.map((option) => (
                    <option key={option.externalTargetId} value={option.externalTargetId}>
                      {option.name} ({option.externalTargetId})
                      {option.isManager ? ' — manager' : ''}
                    </option>
                  ))}
                </select>
              )}
              {selectedOption?.isManager ? (
                <p className="mt-1 text-xs text-amber-700">
                  Manager accounts organise other accounts and do not hold creatives. Pick the
                  advertiser account underneath it instead, or every upload to this target will be
                  rejected.
                </p>
              ) : null}
            </label>
          ) : null}

          <label className="block">
            <span className="mb-1 block text-sm font-medium">Name</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="How this destination appears to users"
            />
          </label>

          {createTarget.error ? <ErrorNotice error={createTarget.error} /> : null}

          <Button
            disabled={!connectionId || !externalTargetId || !name.trim() || createTarget.isPending}
            onClick={submit}
          >
            {createTarget.isPending ? 'Adding…' : 'Add target'}
          </Button>
        </Card>
      </section>

      <section>
        <SectionHeading title="Targets" />
        {targets.data?.targets.length === 0 ? (
          <EmptyState title="No targets yet" />
        ) : (
          <div className="grid gap-2">
            {targets.data?.targets.map((target) => (
              <Card key={target.id} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <PlatformIcon platform={target.platform} />
                  <div>
                    <p className="font-medium">{target.name}</p>
                    <p className="text-xs text-slate-500">
                      {PLATFORM_LABELS[target.platform]} · {target.externalTargetId}
                      {target.config.loginCustomerId
                        ? ` · via manager ${target.config.loginCustomerId}`
                        : ''}
                    </p>
                  </div>
                </div>
                <Button
                  variant="danger"
                  disabled={deleteTarget.isPending}
                  onClick={() => deleteTarget.mutate(target.id)}
                >
                  Remove
                </Button>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
